import { SegmentDto } from '../dtos/segment'
import { HIDDEN_IN_CREATION_FLOWS, SHOW_FIRST_IN_DROPDOWN } from '../constants'
import { ClockCircleOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons'
import React from 'react'
import { SegmentType } from '@pushly/models/lib/enums/segment-type'
import { OptionGroupData } from '@pushly/aqe/lib/components/combo-segment-selection/combo-segment-selection'
import type { FilterSelectOption } from '@pushly/aqe/lib/components/filter-bar/types'

type ReducedSegmentOptions = {
    displaySegments: {
        available: {
            recentlyUsed: SegmentDto[]
            all: SegmentDto[]
        }
    }
    systemSegments: SegmentDto[]
}

const buildOption = (segment: SegmentDto) => ({
    value: segment.id,
    label: segment.name,
    title: segment.name,
})

/**
 * Categorizes segments into recently targeted, system, and all segments groups.
 * Used by both getSegmentFilterOptions and getAudienceSegmentOptionGroups.
 */
const categorizeSegments = (segments: SegmentDto[]) => {
    const latestSegmentsUsed = [...segments]
        .sort((a, b) => {
            const aDate = a.lastTargetedDateUtc ? new Date(a.lastTargetedDateUtc).getTime() : 0
            const bDate = b.lastTargetedDateUtc ? new Date(b.lastTargetedDateUtc).getTime() : 0
            return bDate - aDate
        })
        .slice(0, 5)
        .map((s) => s.id)

    return segments.reduce<ReducedSegmentOptions>(
        (acc, next) => {
            if (
                next.type !== SegmentType.STANDARD &&
                !next.flags.includes(HIDDEN_IN_CREATION_FLOWS) &&
                next.flags.includes(SHOW_FIRST_IN_DROPDOWN)
            ) {
                acc.systemSegments.push(next)
            } else {
                if (latestSegmentsUsed.includes(next.id)) {
                    acc.displaySegments.available.recentlyUsed.push(next)
                } else {
                    acc.displaySegments.available.all.push(next)
                }
            }
            return acc
        },
        {
            displaySegments: {
                available: {
                    recentlyUsed: [],
                    all: [],
                },
            },
            systemSegments: [],
        } as ReducedSegmentOptions,
    )
}

/**
 * Returns flat FilterSelectOption array with group metadata for use with FilterBar.
 * Groups segments into: Recently Targeted, System, and All Segments.
 */
export const getSegmentFilterOptions = (segments: SegmentDto[]): FilterSelectOption[] => {
    if (!segments.length) {
        return []
    }

    const { displaySegments, systemSegments } = categorizeSegments(segments)
    const options: FilterSelectOption[] = []

    // Recently Targeted group (preserve date order)
    const recentlyTargetedLabel = (
        <span>
            <ClockCircleOutlined /> RECENTLY TARGETED
        </span>
    )
    for (const segment of displaySegments.available.recentlyUsed) {
        options.push({
            value: segment.id,
            label: segment.name,
            searchText: segment.name,
            group: 'recently-targeted',
            groupLabel: recentlyTargetedLabel,
        })
    }

    // System group (alpha sorted)
    const systemLabel = (
        <span>
            <SettingOutlined /> SYSTEM
        </span>
    )
    const sortedSystem = [...systemSegments].sort((a, b) => (a.name < b.name ? -1 : 1))
    for (const segment of sortedSystem) {
        options.push({
            value: segment.id,
            label: segment.name,
            searchText: segment.name,
            group: 'system',
            groupLabel: systemLabel,
        })
    }

    // All Segments group (alpha sorted)
    const allSegmentsLabel = (
        <span>
            <TeamOutlined /> ALL SEGMENTS
        </span>
    )
    const sortedAll = [...displaySegments.available.all].sort((a, b) => (a.name < b.name ? -1 : 1))
    for (const segment of sortedAll) {
        options.push({
            value: segment.id,
            label: segment.name,
            searchText: segment.name,
            group: 'all-segments',
            groupLabel: allSegmentsLabel,
        })
    }

    return options
}

export const getAudienceSegmentOptionGroups = (
    segments: SegmentDto[],
    nestedKey: 'children' | 'options' = 'children',
): any[] => {
    if (!segments.length) {
        return []
    }

    const { displaySegments, systemSegments } = categorizeSegments(segments)

    // don't alpha sort recently used, should order by date
    const recentlyUsedOptions = displaySegments.available.recentlyUsed.map(buildOption)
    const systemOptions = [...systemSegments].sort((a, b) => (a.name < b.name ? -1 : 1)).map(buildOption)
    const domainOptions = [...displaySegments.available.all].sort((a, b) => (a.name < b.name ? -1 : 1)).map(buildOption)

    const groups = [] as any[]
    if (recentlyUsedOptions.length) {
        groups.push({
            label: (
                <span>
                    <ClockCircleOutlined /> RECENTLY TARGETED
                </span>
            ),
            key: 'sg-recently-used',
            [nestedKey]: recentlyUsedOptions,
        })
    }

    if (systemOptions.length) {
        groups.push({
            label: (
                <span>
                    <SettingOutlined /> SYSTEM
                </span>
            ),
            key: 'sg-system',
            [nestedKey]: systemOptions,
        })
    }

    if (domainOptions.length) {
        groups.push({
            label: (
                <span>
                    <TeamOutlined /> ALL SEGMENTS
                </span>
            ),
            key: 'sg-all',
            [nestedKey]: domainOptions,
        })
    }

    return groups
}
